import React, { useState, FC } from 'react'
import { AxisBottom, AxisLeft, TickFormatter, TickLabelProps, TickRendererProps } from '@visx/axis'
import * as allCurves from '@visx/curve'
import { localPoint } from '@visx/event'
import { GridRows } from '@visx/grid'
import { Group } from '@visx/group'
import { Point } from '@visx/point'
import { withParentSize, WithParentSizeProvidedProps } from '@visx/responsive'
import { scaleLinear, scaleTime } from '@visx/scale'
import { LinePath, Line } from '@visx/shape'
import { TextProps } from '@visx/text/lib/Text'
import { useTooltip, TooltipWithBounds, defaultStyles } from '@visx/tooltip'
import { max, bisector } from 'd3-array'
import { ScaleTime } from 'd3-scale'
import _chunk from 'lodash/chunk'
import _minBy from 'lodash/minBy'
import { DatumType } from 'transfix-charts/types'
import { Box, Skeleton } from 'transfix-ui/core'
import { avatarColors, brandColors, shadows } from 'transfix-ui/theme'
import { LegendItem } from '../LegendItem/LegendItem'
import { TooltipData } from '../Tooltip/types'
import NoData from './assets/NoData.svg'
import SparkLineLoadingState from './assets/SparkLineLoadingState.svg'
import { LineWithColor, SparkLineData } from './types'

const STYLE_PRESETS = {
  lineStrokeWidth: 1.5,
  lineCurveStyle: allCurves.curveLinear,
  bottomMargin: 40,
  axisLabelMargin: 25,
  width: 400,
  svgHeight: 130,
  focusOpacity: 1,
  backgroundOpacity: 0.3,
  xAxisMargin: 30,
  pointRadius: 4,
  loadingSkeletonWidth: 26,
}

const LINE_COLORS = [
  brandColors.skyBlue6,
  avatarColors.steelBlue,
  avatarColors.iceBlue,
  avatarColors.lavenderPurple,
  avatarColors.plumPurple,
  avatarColors.bubblegumPink,
  avatarColors.roseRed,
  avatarColors.salmonRed,
  avatarColors.orange,
  avatarColors.artichokeGreen,
  avatarColors.spruceGreen,
]

/**
 * Calculate the euclidean distance between two points.
 */
type SimplePoint = { x: number; y: number }
const distance = (point0: SimplePoint, point1: SimplePoint): number =>
  Math.sqrt((point1.x - point0.x) ** 2 + (point1.y - point0.y) ** 2)

/**
 * Returns an index mod number of colors, so if more than 11 lines are being
 * rendered, it will repeat a color that has already been used.
 */
const lineColorIdx = (idx: number): number => idx % LINE_COLORS.length

const getY = ({ y }: DatumType): number => y ?? 0
const getX = ({ x }: DatumType): Date => new Date(x)
const bisectX = bisector<DatumType, Date>(getX).left

// Returns an array of X values corresponding to the first, last and middle
// date in the chart.
const getBottomAxisValues = ([startDate, endDate]: [Date, Date]): Date[] => [
  startDate,
  new Date((startDate.getTime() + endDate.getTime()) / 2),
  endDate,
]

/**
 * Borrowed this implementation from here https://stackoverflow.com/a/9461657
 */
const kFormatter: TickFormatter<any> = num =>
  Math.abs(num) > 999 ? `${(num / 1000).toFixed(1)}k` : `${num}`

// Doesn't really matter what the generic argument is because we're ignoring
// the first argument of the function.
const bottomAxisTickLabelProps: TickLabelProps<any> = (_, idx) => {
  let dx = 0
  let textAnchor: TextProps['textAnchor'] = 'start'
  // Adds some left padding to the first tick label
  if (idx === 0) {
    dx = -5
  }
  // Adds some right padding and changes the text anchor of the last tick
  // label. 'end' textAnchor is analagous to text-align right, which will force
  // the last tick label to be visible within the bounds of the chart.
  if (idx === 2) {
    dx = -5
    textAnchor = 'end'
  }
  return {
    dx,
    textAnchor,
    className: 'text-caption',
    fill: brandColors.coolGray5,
  }
}

// In order to render the loading state, we create some fake data to populate
// the chart while it's loading, and then render the `loadingTickComponent` on
// each axis
const FAKE_LOADING_X_DOMAIN = 50
const DATE_MSEC = 1000 * 60 * 60 * 24
const dateNow = new Date()
const fakeLoadingDateRange = [dateNow, new Date(dateNow.valueOf() + DATE_MSEC * 5)]

const loadingTickComponent = ({
  dx = 0,
  dy = 0,
  textAnchor,
  x,
  y,
}: TickRendererProps): React.ReactNode => (
  <svg
    x={x + (dx as number) - (textAnchor === 'end' ? STYLE_PRESETS.loadingSkeletonWidth : 0)}
    y={y + (dy as number) - 12}
    style={{ overflow: 'visible' }}>
    <foreignObject width={STYLE_PRESETS.loadingSkeletonWidth} height={15}>
      <Skeleton
        variant='text'
        width={STYLE_PRESETS.loadingSkeletonWidth}
        height={15}
        animation='wave'
      />
    </foreignObject>
  </svg>
)

/**
 * Given an xScale, returns a function which determines the closest point in
 * the data set, to the given point in that xScale.
 */
const closestPointAtScale = (xScale: ScaleTime<number, number, never>) => (
  data: DatumType[],
  point: Point
): DatumType => {
  const x0 = xScale.invert(point.x)
  const index = bisectX(data, x0)

  const d0 = data[index - 1]
  const d1 = data[index] ?? { x: 0, y: 0 }

  return d0 && Math.abs(xScale(getX(d0)) - point.x) < Math.abs(xScale(getX(d1)) - point.x) ? d0 : d1
}

interface IResponsiveSparkLineProps {
  /**
   * List of lines that will be shown in the chart. Each line is represented by a key
   * that must be unique and a data array, representing the x, y data points
   * for that line.
   *
   * @default []
   */
  lines: SparkLineData[]
  /**
   * Set to true if a request being made to populate the data in the chart is
   * in flight.
   *
   * @default false
   */
  isLoading: boolean
  /**
   * Defines a React component that is rendered as a tooltip when hovering over
   * the chart. This should display a more detailed view of th data under the
   * cursor.
   */
  renderTooltip: (tooltipData: TooltipData) => React.ReactNode
  /**
   * The data we get from the backend may not span the entire selected date
   * range. Pass in a [minDate, maxDate] array to override the date range
   * detected from the received data.
   */
  dateRange: [Date, Date]
  /**
   * Function returning a string label for each line.
   */
  getLegendLabel: (line: SparkLineData) => string
  /**
   * Function to format the text displayed for each tick on the x axis
   */
  formatXAxisTick: TickFormatter<any>
  /**
   * A special prop that can be passed in if the user has not yet completed any
   * shipments. When this is true, then the empty state looks different,
   * indicating that the user doesn't have any data to display. Otherwise, we
   * show the regular empty state, which indicates that no data could be
   * returned from the selected filters.
   */
  isOnboarding?: boolean
  /**
   * When true the legend is hidden
   * @default false
   */
  hideLegend?: boolean
}
interface ISparkLineProps extends IResponsiveSparkLineProps, WithParentSizeProvidedProps {}

const SparkLine: FC<ISparkLineProps> = ({
  isLoading = false,
  isOnboarding = false,
  lines = [],
  renderTooltip,
  parentWidth = STYLE_PRESETS.width,
  dateRange,
  getLegendLabel,
  formatXAxisTick,
  hideLegend = false,
}) => {
  const linesWithColors: LineWithColor[] = lines.map(({ ...lineData }, idx) => ({
    ...lineData,
    color: LINE_COLORS[lineColorIdx(idx)],
  }))

  /* CONSOLE WARNINGS */
  /* eslint-disable no-console */
  const allLineKeys = [...new Set(lines.map(({ key }) => key))]
  if (allLineKeys.length !== lines.length) {
    console.warn(
      'All keys in `lines` prop must be unique, otherwise some hovering effects will not work'
    )
  }
  if (lines.length > LINE_COLORS.length) {
    console.warn(
      `Only supports ${LINE_COLORS.length} lines. Additional lines will not have unique colors`
    )
  }
  /* eslint-enable */

  const height = STYLE_PRESETS.svgHeight + STYLE_PRESETS.bottomMargin

  /* Hooks */
  const [hoveredLine, setHoveredLine] = useState<string | undefined>()
  const { hideTooltip, showTooltip, tooltipData, tooltipLeft = 0, tooltipTop = 0 } = useTooltip<
    TooltipData
  >()

  /**
   * SCALING FUNCTIONS
   *
   * From the docs (https://github.com/d3/d3-scale#d3-scale)
   * "Scales are a convenient abstraction for a fundamental task in
   * visualization: mapping a dimension of abstract data to a visual
   * representation"
   *
   * What this means more concretely, is scaling functions give you a way to
   * map an x, y pair in your data to an x, y coordinate on the SVG.
   *
   * Each scaling function takes a range and a domain config. The range
   * represents the range of coordinates on the SVG, while the domain
   * represents the range of values in the dataset. d3-scale functions
   * automatically map a value in the dataset to it's appropriate x, y
   * coordinate in the SVG (and vice versa in the case of `yScale.invert`.
   */
  const allValues = lines.flatMap(({ data }) => data)
  // Use a default max domain of 100 so a chart with only 0 values has a Y
  // axis with more than just a 0 line.
  const maxDomainValue = ((max(allValues, getY) ?? 0) > 0 ? max(allValues, getY) : 100) ?? 0
  const yScale = scaleLinear<number>({
    range: [height, 0],
    domain: [0, isLoading ? FAKE_LOADING_X_DOMAIN : maxDomainValue],
    // Extends the domain so it starts and ends on nice round numbers
    nice: true,
  })

  const xScale = scaleTime({
    range: [STYLE_PRESETS.xAxisMargin, parentWidth],
    // All of the lines should cover the same date ranges, so we can just use
    // the first chart to determine the approprate domain.
    domain: isLoading ? fakeLoadingDateRange : dateRange,
  })

  // Uses xScale to calculate the closest point in the data set to the cursor
  const getClosestPoint = closestPointAtScale(xScale)

  /* EVENT HANDLERS */
  const mouseOverHandler = (event: React.MouseEvent) => {
    // Get the local point within the svg of the mouse event
    const point = localPoint(event)

    if (point) {
      // Find the index of the data array, with which this point bisects
      const closestChart = _minBy(linesWithColors, ({ data }) => {
        const closestDatum = getClosestPoint(data, point)
        const y0 = point.y
        const y1 = yScale(getY(closestDatum))

        const x0 = point.x
        const x1 = xScale(getX(closestDatum))

        return distance({ x: x0, y: y0 }, { x: x1, y: y1 })
      })

      if (!closestChart) {
        return
      }

      const { color: closestLineColor, data: closestData, key: closestLineKey } = closestChart

      const closestDatum = getClosestPoint(closestData, point)

      setHoveredLine(closestLineKey)

      // Find closest value in the datum to the svg coordinates
      const updatedTooltipData = {
        x: closestDatum.x,
        y: closestDatum.y,
        circleColor: closestLineColor,
        label: closestLineKey,
      }

      showTooltip({
        tooltipData: updatedTooltipData,
        tooltipTop: yScale(getY(closestDatum)),
        tooltipLeft: xScale(getX(closestDatum)),
      })
    }
  }

  const mouseLeaveHandler = () => {
    // Only do this if we're leaving the chart-container. This handler is
    // captured when the mouse leaves _any_ child component of the
    // chart-container svg. This includes a the LinePath component, which we
    // obviously don't want to trigger this.
    if (hoveredLine) {
      setHoveredLine(undefined)
      hideTooltip()
    }
  }

  if (!isLoading && lines.length === 0) {
    return (
      <div
        style={{ width: parentWidth, height }}
        className='flex flex-column items-center justify-center'>
        {isOnboarding ? (
          <>
            <img src={SparkLineLoadingState} alt='Loading...' />
            <span className='body-large text-secondary mt4'>Your data will appear here</span>
          </>
        ) : (
          <>
            <img src={NoData} alt='No Data' />
            <span className='body-large text-secondary mt4'>No Data</span>
          </>
        )}
      </div>
    )
  }

  return (
    <div>
      <svg
        id='chart-container'
        onMouseLeave={mouseLeaveHandler}
        width={parentWidth}
        height={height + STYLE_PRESETS.axisLabelMargin}>
        <Group onMouseMove={mouseOverHandler}>
          <GridRows scale={yScale} width={parentWidth} height={height} numTicks={3} />
          <AxisLeft
            hideTicks
            hideAxisLine
            scale={yScale}
            left={15}
            numTicks={3}
            // Default font size is 14px and tick labels are, by default,
            // rendered centered with the grid line. So offset the y position
            // of the tick by half of the font size, to ensure that it sits on
            // top of the grid line
            tickLabelProps={() => ({
              className: 'text-caption',
              fill: brandColors.coolGray5,
              textAnchor: 'start',
              dy: -7,
            })}
            tickFormat={kFormatter}
            tickComponent={isLoading ? loadingTickComponent : undefined}
          />
          {!isLoading &&
            linesWithColors.map(({ color: lineColor, data, key }) => (
              <LinePath
                key={key}
                curve={STYLE_PRESETS.lineCurveStyle}
                data={data}
                x={d => xScale(getX(d))}
                y={d => yScale(getY(d))}
                stroke={lineColor}
                strokeWidth={STYLE_PRESETS.lineStrokeWidth}
                strokeOpacity={
                  !hoveredLine || hoveredLine === key
                    ? STYLE_PRESETS.focusOpacity
                    : STYLE_PRESETS.backgroundOpacity
                }
              />
            ))}
          <AxisBottom
            hideTicks
            hideAxisLine
            top={height}
            orientation='bottom'
            scale={xScale}
            // Just the first middle and last ticks.
            tickValues={getBottomAxisValues(dateRange)}
            tickFormat={formatXAxisTick}
            tickLabelProps={bottomAxisTickLabelProps}
            tickComponent={isLoading ? loadingTickComponent : undefined}
          />
        </Group>
        {tooltipData && (
          <g>
            <Line
              from={{ x: tooltipLeft, y: 0 }}
              to={{ x: tooltipLeft, y: height }}
              stroke={brandColors.coolGray4}
              strokeWidth={1}
            />
            <circle
              cx={tooltipLeft}
              cy={tooltipTop}
              r={STYLE_PRESETS.pointRadius + 2}
              fill={brandColors.white}
            />
            <circle
              cx={tooltipLeft}
              cy={tooltipTop}
              r={STYLE_PRESETS.pointRadius}
              fill={tooltipData.circleColor}
            />
          </g>
        )}
      </svg>
      {tooltipData && (
        <TooltipWithBounds
          style={{ ...defaultStyles, boxShadow: shadows[10], color: brandColors.coolGray8 }}
          key={tooltipData.x}
          top={tooltipTop + 24}
          left={tooltipLeft}>
          {renderTooltip(tooltipData)}
        </TooltipWithBounds>
      )}
      {!hideLegend && (
        <div className='ph5 pb5 pt4'>
          {!isLoading &&
            _chunk(linesWithColors, 2).map(([line1, line2]) => (
              <Box display='flex' flexDirection='row' flex={1} key={`${line1.key}-${line2?.key}`}>
                <LegendItem label={getLegendLabel(line1)} color={line1.color} />
                {line2 && <LegendItem label={getLegendLabel(line2)} color={line2.color} />}
              </Box>
            ))}
        </div>
      )}
    </div>
  )
}

// VisX doesn't provide a hook for this yet, so we're stuck with using this
// function for now.
export const ResponsiveSparkLine: FC<IResponsiveSparkLineProps> = withParentSize(SparkLine)

export default SparkLine
